/**
 * src/components/spatial/spatialFeatureDerivation.ts
 *
 * The decisions `normalizeSpatialFeatures` makes, pulled out of it.
 *
 * All are pure functions of a metadata blob, a clock, or what the caller
 * has already looked up, and all are easy to get subtly wrong in ways a
 * map does not report: a flag drawn upside down, a tower wearing an
 * arrangement nobody chose, or an event that says it is live a day after
 * it finished. Here they can be checked without a map, a canvas or a
 * network.
 */

import { parseFlagLayout, type FlagLayout } from '../../utils/flagArrangements';

/** An occasion is "soon" from here until it starts. */
export const EVENT_SOON_WINDOW_MS = 6 * 60 * 60_000;

/**
 * How long an occasion with no stated end is treated as live.
 *
 * An undated finish is an omission, not a claim of permanence, and a
 * building that stays lit for a party that ended last month is worse than
 * one that goes quiet a few hours early.
 */
export const NO_END_LIVE_MS = 4 * 60 * 60_000;

export type EventState = 'live' | 'soon' | 'past';

/**
 * Where an object's occasion is relative to now.
 *
 * `undefined` for an object with no dated occasion — and for one whose
 * start is still far off — so the layer expressions can treat the
 * property's absence as "nothing to say" without a second flag.
 */
/**
 * Statuses under which the clock may make an event live.
 *
 * Mirrors `server/services/eventModel.ACTIVE_STATUSES`. No import
 * crosses the server/client boundary in this repo, so the two lists are
 * checked against each other by a test instead.
 */
const ACTIVE_EVENT_STATUSES = new Set(['scheduled', 'live']);

export function deriveEventState(
  meta: Record<string, unknown>,
  now: number,
): EventState | undefined {
  const ev = meta.event as {
    startsAt?: unknown; endsAt?: unknown; status?: unknown;
  } | undefined;
  if (!ev || typeof ev !== 'object') return undefined;

  // A cancelled event has perfectly good timestamps, so painting from
  // those alone lights a building up for a concert that is not
  // happening. An absent status predates the lifecycle and was simply
  // scheduled; treating it as inactive would blank every existing
  // event on the map.
  if (typeof ev.status === 'string' && ev.status.trim()) {
    if (!ACTIVE_EVENT_STATUSES.has(ev.status.trim().toLowerCase())) return undefined;
  }

  const start = typeof ev.startsAt === 'string' ? Date.parse(ev.startsAt) : NaN;
  if (!Number.isFinite(start)) return undefined;

  const parsedEnd = typeof ev.endsAt === 'string' ? Date.parse(ev.endsAt) : NaN;
  // An end before the start is a typo, not a window; the nominal one is
  // used instead so the occasion is not born already over.
  const end = Number.isFinite(parsedEnd) && parsedEnd > start
    ? parsedEnd
    : start + NO_END_LIVE_MS;

  if (now >= end) return 'past';
  if (now >= start) return 'live';
  if (start - now <= EVENT_SOON_WINDOW_MS) return 'soon';
  return undefined;
}

export { ACTIVE_EVENT_STATUSES };

/**
 * Every derived property an event contributes to a map feature.
 *
 * Grouped in one function so the normalizer stamps a consistent set
 * rather than each caller choosing which of them to compute, and so
 * the registry in `derivedPresentation` has exactly one producer to
 * correspond to.
 *
 * Nothing here is written back. These are read from stored metadata,
 * combined with the current time, and stamped onto the GeoJSON the map
 * renders — the transient layer composing over the permanent one.
 */
export function deriveEventPresentation(
  meta: Record<string, unknown>,
  now: number,
  /**
   * How many people are at this event, if anybody has asked.
   *
   * Passed in rather than read from `meta`, and that is the whole
   * design. A count is a reading taken now, not a fact about a
   * building: storing it would put a number that changes every minute
   * into a column that describes a place, and the next person to read
   * the metadata would believe it.
   *
   * `undefined` means nobody asked, which is different from zero. An
   * event nobody has counted should not be painted as empty.
   */
  eventCount?: number,
): {
  eventState?: EventState;
  eventLifecycle?: string;
  eventIsPart?: boolean;
  eventCount?: number;
} {
  const ev = meta?.event as {
    status?: unknown; parentEventId?: unknown; parent_event_id?: unknown;
  } | undefined;
  if (!ev || typeof ev !== 'object') return {};

  const derived: {
    eventState?: EventState;
    eventLifecycle?: string;
    eventIsPart?: boolean;
    eventCount?: number;
  } = {};

  const state = deriveEventState(meta, now);
  if (state) derived.eventState = state;

  // Only a real count, and only a sane one. A negative or fractional
  // attendance is a bug upstream, and painting it would make the map
  // the place somebody discovers that.
  if (Number.isInteger(eventCount) && (eventCount as number) >= 0) {
    derived.eventCount = eventCount;
  }

  // Carried even when there is no state, because "cancelled" is the
  // explanation for the absence and a panel that cannot see it has to
  // guess between cancelled, finished and never scheduled.
  const status = typeof ev.status === 'string' ? ev.status.trim().toLowerCase() : '';
  if (status) derived.eventLifecycle = status;

  const parent = ev.parentEventId ?? ev.parent_event_id;
  if (typeof parent === 'string' && parent.trim()) derived.eventIsPart = true;

  return derived;
}

/** A flag a building is wearing, resolved down to what gets painted. */
export interface FlagPaint {
  countryCode: string;
  /** Top of the building first. */
  bands: string[];
  /**
   * How those colours meet the building.
   *
   * Always present: a flag recorded before layouts existed, or one falling
   * back to a palette, resolves to horizontal bands, which is what such a
   * building was already wearing.
   */
  layout: FlagLayout;
}

/** Colour strings only, since these end up in a paint property. */
function bandsIn(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((c): c is string => typeof c === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(c));
}

/**
 * The country a feature says it is wearing, or the one remembered for it.
 *
 * `metadata.surface_flag` is the render hint the presence editor mirrors
 * alongside the authoritative GeoLink surface config, because neither
 * `/render` nor `/api/map` carries that config and fetching it per visible
 * building would be one request per tower.
 *
 * The bulk viewport response carries no metadata at all, though, so the
 * remembered intent is consulted second — otherwise every refresh would
 * undress a building that had been enriched moments earlier.
 */
export function flagCountryFor(
  meta: Record<string, unknown>,
  countryCode: string | undefined,
  remembered: { countryCode?: string } | null,
): string | null {
  const raw = (meta.surface_flag ?? meta.surfaceFlag) as
    | { enabled?: unknown; countryCode?: unknown; country_code?: unknown }
    | undefined;

  if (raw && typeof raw === 'object' && raw.enabled === true) {
    for (const c of [raw.countryCode, raw.country_code, countryCode]) {
      if (typeof c !== 'string') continue;
      const s = c.trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(s)) return s;
    }
  }

  const held = String(remembered?.countryCode ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(held) ? held : null;
}

/**
 * The flag this object should be wearing, if any.
 *
 * Bands recorded on the object win over anything derived here. The editor
 * offers several arrangements of the same flag and stores the one that was
 * previewed, so re-deriving would repaint the tower in whichever
 * arrangement happens to be the default — a different building from the
 * one the operator approved. The palette is the fallback for buildings
 * flagged before arrangements existed, which recorded a country and
 * nothing more.
 *
 * Null while a fallback palette is still loading: the render path is
 * synchronous, and a building briefly wearing its normal colour is better
 * than one wearing invented colours.
 *
 * The lookups are passed in rather than reached for, which is what keeps
 * this checkable without a browser.
 */
export function resolveFlagPaint(
  meta: Record<string, unknown>,
  countryCode: string | undefined,
  remembered: { countryCode?: string; bands?: readonly string[]; layout?: unknown } | null,
  palette: (iso: string) => readonly string[] | null,
): FlagPaint | null {
  const iso = flagCountryFor(meta, countryCode, remembered);
  if (!iso) return null;

  const raw = (meta.surface_flag ?? meta.surfaceFlag) as
    | { bands?: unknown; layout?: unknown }
    | undefined;
  const object = raw && typeof raw === 'object' ? raw : undefined;

  // A layout is only honoured beside the bands it was chosen with, so a
  // stale layout on the object cannot rearrange bands the cache is holding,
  // or vice versa. Either source describes one approved building.
  const stated = object ? bandsIn(object.bands) : [];
  if (object && stated.length > 0) {
    return { countryCode: iso, bands: stated, layout: layoutFor(object.layout, stated) };
  }

  const held = bandsIn(remembered?.bands);
  if (held.length > 0) {
    return { countryCode: iso, bands: held, layout: layoutFor(remembered?.layout, held) };
  }

  const derived = bandsIn(palette(iso));
  return derived.length > 0
    ? { countryCode: iso, bands: derived, layout: { kind: 'bands', bands: derived } }
    : null;
}

/** A stored layout if it is usable, else the bands as horizontal bands. */
function layoutFor(stored: unknown, bands: string[]): FlagLayout {
  return parseFlagLayout(stored) ?? { kind: 'bands', bands };
}

export interface BandSlab {
  /** Bottom of the slab, in metres above ground. */
  low: number;
  /** Top of the slab, in metres above ground. */
  high: number;
  /** Index into the band list — 0 is the top of the flag. */
  bandIndex: number;
  /** Index in the same order tier expansion uses, counting up from the ground. */
  tierIndex: number;
}

/**
 * Slice one building's height into a slab per flag band.
 *
 * A building with no mapped setbacks has a single slab, so a band per
 * tier would wash the whole tower in one colour. Slicing produces exactly
 * the per-slab features the tier expansion already renders, which is what
 * lets a flag land on a plain box with no textures and no new layer.
 *
 * Band 0 is the top of the flag and takes the highest slab, so the flag
 * reads the right way up on a facade.
 */
export function flagBandSlabs(
  baseMeters: number,
  heightMeters: number,
  bandCount: number,
): BandSlab[] {
  const from = Number.isFinite(baseMeters) ? baseMeters : 0;
  const span = heightMeters - from;
  if (!(span > 0) || !(bandCount > 0)) return [];

  const n = Math.floor(bandCount);
  return Array.from({ length: n }, (_, i) => ({
    low: from + (span * (n - 1 - i)) / n,
    high: from + (span * (n - i)) / n,
    bandIndex: i,
    tierIndex: n - 1 - i,
  }));
}

/** Below this, a slab is a sliver of rounding rather than a visible stripe. */
const MIN_SLAB_METERS = 0.5;

/**
 * The part of one tier that falls in each band of a flag worn by the
 * whole building.
 *
 * A flag belongs to the tower, not to its parts: the bands are cuts at
 * fixed heights, and a tier crosses however many of them its own extent
 * reaches. Colouring a tier as a unit instead — one band per part —
 * washes a tall shaft in a single stripe, which is why the Burj Khalifa
 * came out white from podium to spire with green only on the setbacks
 * short enough to sit inside the ground band.
 *
 * Bands are measured from the ground to `peakMeters` so that every tier
 * agrees where the stripes are; a tier reaching only partway up simply
 * returns the bands it touches.
 */
export function flagBandSlabsWithin(
  lowMeters: number,
  highMeters: number,
  peakMeters: number,
  bandCount: number,
): BandSlab[] {
  const n = Math.floor(bandCount);
  if (!(n > 0) || !(peakMeters > 0)) return [];

  const low = Number.isFinite(lowMeters) ? Math.max(0, lowMeters) : 0;
  const high = Number.isFinite(highMeters) ? highMeters : NaN;
  if (!(high > low)) return [];

  const out: BandSlab[] = [];
  // Band 0 is the top of the flag, so it takes the highest cut.
  for (let i = 0; i < n; i += 1) {
    const bandLow = (peakMeters * (n - 1 - i)) / n;
    const bandHigh = (peakMeters * (n - i)) / n;
    const slabLow = Math.max(low, bandLow);
    // The topmost band absorbs anything above the peak, so a spire taller
    // than the tallest recorded part is painted rather than cut off. Only
    // for a tier that reaches band 0 at all — extending it unconditionally
    // gave every podium a phantom slab in the sky.
    const slabHigh = i === 0 && high > bandHigh ? high : Math.min(high, bandHigh);
    if (slabHigh - slabLow < MIN_SLAB_METERS) continue;
    out.push({ low: slabLow, high: slabHigh, bandIndex: i, tierIndex: out.length });
  }

  // Reported ground-up, matching how tier expansion numbers its parts.
  const total = out.length;
  return out.map((s, k) => ({ ...s, tierIndex: total - 1 - k }));
}
